import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell
} from 'recharts';
import {
  TrendingUp, TrendingDown, Clock, Target, Award, AlertTriangle,
  CheckCircle, Info, Zap, Timer, ArrowRight, DollarSign, PieChart,
  UserCircle, Briefcase
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

const STAGE_CONFIG = {
  new: { label: 'New', color: '#64748b', bg: 'bg-slate-500/15 text-slate-300' },
  contacted: { label: 'Contacted', color: '#3b82f6', bg: 'bg-blue-500/15 text-blue-400' },
  meeting_scheduled: { label: 'Meeting', color: '#8b5cf6', bg: 'bg-violet-500/15 text-violet-400' },
  proposal_sent: { label: 'Proposal', color: '#f59e0b', bg: 'bg-amber-500/15 text-amber-400' },
  won: { label: 'Won', color: '#10b981', bg: 'bg-emerald-500/15 text-emerald-400' },
  lost: { label: 'Lost', color: '#ef4444', bg: 'bg-red-500/15 text-red-400' },
};

const STAGE_ORDER = ['new', 'contacted', 'meeting_scheduled', 'proposal_sent', 'won', 'lost'];
const TIME_RANGES = [
  { label: '7 days', value: 7 },
  { label: '30 days', value: 30 },
  { label: '90 days', value: 90 },
  { label: 'YTD', value: 365 },
];

function getAuthHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('admin_token')}` };
}

// KPI Card
function KPICard({ icon: Icon, label, value, subtext, color }) {
  const colorMap = {
    cyan: 'bg-cyan-500/10 text-cyan-400',
    emerald: 'bg-emerald-500/10 text-emerald-400',
    red: 'bg-red-500/10 text-red-400',
    violet: 'bg-violet-500/10 text-violet-400',
    amber: 'bg-amber-500/10 text-amber-400',
    blue: 'bg-blue-500/10 text-blue-400',
  };
  return (
    <div className="p-4 sm:p-5 rounded-xl bg-slate-900 border border-slate-800" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className={`w-8 h-8 rounded-lg ${colorMap[color]} flex items-center justify-center`}>
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-slate-400 text-xs sm:text-sm font-medium">{label}</span>
      </div>
      <div className="text-xl sm:text-2xl font-bold text-white">{value}</div>
      {subtext && <p className="text-slate-500 text-xs mt-1">{subtext}</p>}
    </div>
  );
}

// Funnel bar tooltip
function FunnelTooltip({ active, payload }) {
  if (!active || !payload?.[0]) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm shadow-xl">
      <p className="text-white font-medium">{d.label}</p>
      <p className="text-slate-300">{d.count} leads</p>
    </div>
  );
}

// Timeline tooltip
function TimeTooltip({ active, payload, label }) {
  if (!active || !payload) return null;
  return (
    <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm shadow-xl">
      <p className="text-white font-medium mb-1">Week of {label}</p>
      {payload.map((p) => (
        <p key={p.name} style={{ color: p.color }} className="text-xs">{p.name}: {p.value}</p>
      ))}
    </div>
  );
}

export default function PipelineAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const fetchData = useCallback(async () => {
    try {
      const { data: d } = await axios.get(`${API}/api/admin/pipeline-analytics?days=${days}`, { headers: getAuthHeaders() });
      setData(d);
    } catch (err) {
      console.error('[PipelineAnalytics] Failed to load:', err?.response?.data || err?.message);
    }
    setLoading(false);
  }, [days]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-cyan-400/30 border-t-cyan-400 rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) return <p className="text-slate-500 text-center py-10">Failed to load analytics</p>;

  const { funnel, leads_over_time, avg_time_in_stage, time_to_close, conversions, insights, revenue } = data;

  // Funnel chart data
  const funnelData = STAGE_ORDER.filter(s => s !== 'lost').map(s => ({
    stage: s,
    label: STAGE_CONFIG[s].label,
    count: funnel[s] || 0,
    color: STAGE_CONFIG[s].color,
  }));

  return (
    <div className="space-y-6" data-testid="pipeline-analytics">
      {/* Time Range Selector */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="text-white font-bold text-lg flex items-center gap-2" data-testid="analytics-title">
          <TrendingUp className="h-5 w-5 text-cyan-400" /> Pipeline Analytics
        </h2>
        <div className="flex gap-1 bg-slate-900 rounded-lg p-1" data-testid="time-range-selector">
          {TIME_RANGES.map(r => (
            <button
              key={r.value}
              onClick={() => setDays(r.value)}
              data-testid={`range-${r.value}`}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                days === r.value ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-white'
              }`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>

      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <KPICard icon={Target} label="Total Leads" value={conversions.total_leads} color="cyan" />
        <KPICard icon={Award} label="Won" value={conversions.total_won} color="emerald" subtext={conversions.total_won > 0 ? `${conversions.overall}% win rate` : undefined} />
        <KPICard icon={TrendingDown} label="Lost" value={conversions.total_lost} color="red" />
        <KPICard icon={TrendingUp} label="Win Rate" value={`${conversions.overall}%`} color="violet" subtext="Overall conversion" />
        <KPICard icon={Timer} label="Avg Close" value={time_to_close.won.avg ? `${time_to_close.won.avg}d` : '—'} color="amber" subtext={time_to_close.won.fastest != null ? `${time_to_close.won.fastest}d fastest` : undefined} />
        <KPICard icon={Clock} label="Avg Lost" value={time_to_close.lost.avg ? `${time_to_close.lost.avg}d` : '—'} color="blue" subtext={time_to_close.lost.count > 0 ? `${time_to_close.lost.count} deals` : undefined} />
      </div>

      {/* Funnel + Stage Conversion */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Pipeline Funnel */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="pipeline-funnel">
          <h3 className="text-white font-semibold mb-4">Pipeline Funnel</h3>
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={funnelData} layout="vertical" margin={{ left: 10, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                <XAxis type="number" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} />
                <YAxis dataKey="label" type="category" tick={{ fill: '#94a3b8', fontSize: 13 }} axisLine={false} width={80} />
                <Tooltip content={<FunnelTooltip />} cursor={false} />
                <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={28}>
                  {funnelData.map((entry) => (
                    <Cell key={entry.label} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          {funnel.lost > 0 && (
            <p className="text-red-400/70 text-xs mt-2 text-center">{funnel.lost} lost deal{funnel.lost > 1 ? 's' : ''} not shown in funnel</p>
          )}
        </div>

        {/* Stage Conversion Rates */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="conversion-rates">
          <h3 className="text-white font-semibold mb-4">Stage Conversion Rates</h3>
          <div className="space-y-4">
            {[
              { from: 'New', to: 'Contacted', rate: conversions.new_to_contacted, color: '#3b82f6' },
              { from: 'Contacted', to: 'Meeting', rate: conversions.contacted_to_meeting, color: '#8b5cf6' },
              { from: 'Meeting', to: 'Proposal', rate: conversions.meeting_to_proposal, color: '#f59e0b' },
              { from: 'Proposal', to: 'Won', rate: conversions.proposal_to_won, color: '#10b981' },
            ].map((c) => (
              <div key={`${c.from}-${c.to}`}>
                <div className="flex items-center justify-between text-sm mb-1.5">
                  <span className="text-slate-300 flex items-center gap-1.5">
                    {c.from} <ArrowRight className="h-3 w-3 text-slate-500" /> {c.to}
                  </span>
                  <span className="text-white font-semibold">{c.rate}%</span>
                </div>
                <div className="h-2.5 rounded-full bg-slate-800 overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(c.rate, 100)}%`, backgroundColor: c.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Leads Over Time + Avg Time in Stage */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Leads Over Time */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="leads-over-time">
          <h3 className="text-white font-semibold mb-4">Leads Over Time</h3>
          {leads_over_time.length > 0 ? (
            <div className="h-[240px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={leads_over_time} margin={{ left: -10, right: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                  <XAxis dataKey="week" tick={{ fill: '#64748b', fontSize: 10 }} axisLine={false} tickFormatter={w => { const d = new Date(w); return `${d.getMonth()+1}/${d.getDate()}`; }} />
                  <YAxis tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} />
                  <Tooltip content={<TimeTooltip />} cursor={false} />
                  <Bar dataKey="new" name="Active" stackId="a" fill="#3b82f6" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="won" name="Won" stackId="a" fill="#10b981" radius={[0, 0, 0, 0]} />
                  <Bar dataKey="lost" name="Lost" stackId="a" fill="#ef4444" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-slate-500 text-sm text-center py-10">No data for this period</p>
          )}
          <div className="flex items-center justify-center gap-4 mt-3 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-blue-500" /> Active</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-emerald-500" /> Won</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-red-500" /> Lost</span>
          </div>
        </div>

        {/* Avg Time in Stage */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="avg-time-in-stage">
          <h3 className="text-white font-semibold mb-4">Average Time in Stage</h3>
          <div className="space-y-3">
            {(() => {
              const maxDays = Math.max(...Object.values(avg_time_in_stage).filter(v => v != null), 1);
              return STAGE_ORDER.filter(s => s !== 'lost').map(stage => {
                const days_val = avg_time_in_stage[stage];
                const cfg = STAGE_CONFIG[stage];
                const pct = days_val != null ? Math.min((days_val / maxDays) * 100, 100) : 0;
                return (
                  <div key={stage}>
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-slate-300">{cfg.label}</span>
                      <span className="text-white font-medium">
                        {days_val != null ? (days_val < 1 ? `${Math.round(days_val * 24)}h` : `${days_val}d`) : '—'}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: cfg.color }} />
                    </div>
                  </div>
              );
              });
            })()}
          </div>
          {Object.values(avg_time_in_stage).every(v => v == null) && (
            <p className="text-slate-500 text-xs mt-3 text-center">Move leads through stages to see time metrics</p>
          )}
        </div>
      </div>

      {/* Time to Close + Sales Insights */}
      <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Time to Close */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="time-to-close">
          <h3 className="text-white font-semibold mb-4">Time to Close</h3>
          <div className="grid grid-cols-2 gap-4">
            {/* Won */}
            <div className="p-4 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
              <p className="text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-3">Won Deals</p>
              {time_to_close.won.count > 0 ? (
                <div className="space-y-2">
                  <div>
                    <span className="text-slate-400 text-xs">Average</span>
                    <p className="text-white text-xl font-bold">{time_to_close.won.avg}d</p>
                  </div>
                  <div className="flex justify-between text-xs">
                    <div><span className="text-slate-500">Fastest</span><p className="text-emerald-400 font-medium">{time_to_close.won.fastest}d</p></div>
                    <div className="text-right"><span className="text-slate-500">Slowest</span><p className="text-amber-400 font-medium">{time_to_close.won.slowest}d</p></div>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No won deals yet</p>
              )}
            </div>
            {/* Lost */}
            <div className="p-4 rounded-lg bg-red-500/5 border border-red-500/20">
              <p className="text-red-400 text-xs font-semibold uppercase tracking-wider mb-3">Lost Deals</p>
              {time_to_close.lost.count > 0 ? (
                <div className="space-y-2">
                  <div>
                    <span className="text-slate-400 text-xs">Average</span>
                    <p className="text-white text-xl font-bold">{time_to_close.lost.avg}d</p>
                  </div>
                  <div className="flex justify-between text-xs">
                    <div><span className="text-slate-500">Fastest</span><p className="text-red-400 font-medium">{time_to_close.lost.fastest}d</p></div>
                    <div className="text-right"><span className="text-slate-500">Slowest</span><p className="text-red-400 font-medium">{time_to_close.lost.slowest}d</p></div>
                  </div>
                </div>
              ) : (
                <p className="text-slate-500 text-sm">No lost deals yet</p>
              )}
            </div>
          </div>
        </div>

        {/* Sales Insights */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="sales-insights">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-400" /> Sales Insights
          </h3>
          <div className="space-y-3">
            {insights.map((insight, i) => {
              const iconMap = {
                positive: <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0 mt-0.5" />,
                warning: <AlertTriangle className="h-4 w-4 text-amber-400 flex-shrink-0 mt-0.5" />,
                info: <Info className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />,
              };
              const bgMap = {
                positive: 'bg-emerald-500/5 border-emerald-500/20',
                warning: 'bg-amber-500/5 border-amber-500/20',
                info: 'bg-blue-500/5 border-blue-500/20',
              };
              return (
                <div key={insight.text} className={`flex items-start gap-3 p-3 rounded-lg border ${bgMap[insight.type]}`} data-testid={`insight-${i}`}>
                  {iconMap[insight.type]}
                  <p className="text-slate-200 text-sm leading-relaxed">{insight.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* ─── Revenue Pipeline Section ─────────────────────── */}
      {revenue && (
        <>
          <div className="flex items-center gap-2 mt-2" data-testid="revenue-pipeline-header">
            <DollarSign className="h-5 w-5 text-emerald-400" />
            <h2 className="text-white font-bold text-lg">Revenue Pipeline</h2>
          </div>

          {/* Revenue KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="revenue-kpis">
            <KPICard icon={Briefcase} label="Open Pipeline" value={`$${(revenue.total_pipeline / 1000).toFixed(0)}k`} color="emerald" subtext="Active deals" />
            <KPICard icon={Award} label="Won Revenue" value={`$${(revenue.won_revenue / 1000).toFixed(0)}k`} color="cyan" subtext={revenue.won_revenue > 0 ? `${((revenue.won_revenue / (revenue.total_pipeline + revenue.won_revenue)) * 100).toFixed(0)}% closed` : undefined} />
            <KPICard icon={Target} label="Weighted Forecast" value={`$${(revenue.weighted_forecast / 1000).toFixed(0)}k`} color="violet" subtext="Probability-adjusted" />
            <KPICard icon={DollarSign} label="Avg Deal Size" value={revenue.avg_deal_size ? `$${revenue.avg_deal_size.toLocaleString()}` : '—'} color="amber" />
            <KPICard icon={TrendingUp} label="Largest Opp" value={revenue.largest_opportunity ? `$${revenue.largest_opportunity.toLocaleString()}` : '—'} color="blue" />
          </div>

          {/* Revenue by Stage + Weighted Forecast by Stage */}
          <div className="grid lg:grid-cols-2 gap-4 sm:gap-6">
            {/* Revenue by Stage */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="revenue-by-stage">
              <h3 className="text-white font-semibold mb-4">Revenue by Stage</h3>
              {(() => {
                const stageData = STAGE_ORDER.map(s => ({
                  stage: s,
                  label: STAGE_CONFIG[s].label,
                  value: (revenue.revenue_by_stage || {})[s] || 0,
                  color: STAGE_CONFIG[s].color,
                })).filter(d => d.value > 0);
                if (stageData.length === 0) return <p className="text-slate-500 text-sm text-center py-10">Add proposal values to leads to see revenue by stage</p>;
                return (
                  <div className="h-[260px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stageData} layout="vertical" margin={{ left: 10, right: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" horizontal={false} />
                        <XAxis type="number" tick={{ fill: '#64748b', fontSize: 12 }} axisLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                        <YAxis dataKey="label" type="category" tick={{ fill: '#94a3b8', fontSize: 13 }} axisLine={false} width={80} />
                        <Tooltip content={({ active, payload }) => {
                          if (!active || !payload?.[0]) return null;
                          const d = payload[0].payload;
                          return <div className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm shadow-xl"><p className="text-white font-medium">{d.label}</p><p className="text-emerald-400">${d.value.toLocaleString()}</p></div>;
                        }} cursor={false} />
                        <Bar dataKey="value" radius={[0, 6, 6, 0]} barSize={28}>
                          {stageData.map((entry) => <Cell key={entry.stage} fill={entry.color} />)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}
            </div>

            {/* Weighted Forecast by Stage */}
            <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="weighted-forecast">
              <h3 className="text-white font-semibold mb-4">Weighted Forecast by Stage</h3>
              <div className="space-y-3">
                {(() => {
                  const weights = { new: '10%', contacted: '25%', meeting_scheduled: '40%', proposal_sent: '70%', won: '100%', lost: '0%' };
                  const maxWeighted = Math.max(...Object.values(revenue.weighted_by_stage || {}).filter(v => v > 0), 1);
                  const stages = STAGE_ORDER.filter(s => s !== 'lost');
                  const hasData = stages.some(s => (revenue.weighted_by_stage || {})[s] > 0);
                  if (!hasData) return <p className="text-slate-500 text-sm text-center py-10">Add proposal values to see weighted forecasts</p>;
                  return stages.map(s => {
                    const raw = (revenue.revenue_by_stage || {})[s] || 0;
                    const weighted = (revenue.weighted_by_stage || {})[s] || 0;
                    const cfg = STAGE_CONFIG[s];
                    const pct = maxWeighted > 0 ? Math.min((weighted / maxWeighted) * 100, 100) : 0;
                    return (
                      <div key={s}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-slate-300">{cfg.label} <span className="text-slate-500 text-xs">({weights[s]})</span></span>
                          <div className="text-right">
                            <span className="text-white font-medium">${weighted.toLocaleString()}</span>
                            {raw > 0 && raw !== weighted && <span className="text-slate-500 text-xs ml-1.5">of ${raw.toLocaleString()}</span>}
                          </div>
                        </div>
                        <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: cfg.color }} />
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>

          {/* Revenue by Owner */}
          <div className="bg-slate-900 rounded-xl border border-slate-800 p-5" data-testid="revenue-by-owner">
            <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
              <UserCircle className="h-4 w-4 text-cyan-400" /> Revenue by Owner
            </h3>
            {(() => {
              const owners = Object.entries(revenue.revenue_by_owner || {});
              if (owners.length === 0) return <p className="text-slate-500 text-sm text-center py-6">Assign leads and add values to see owner performance</p>;
              const maxTotal = Math.max(...owners.map(([, d]) => d.total), 1);
              return (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="owner-revenue-table">
                    <thead>
                      <tr className="text-left border-b border-slate-800">
                        <th className="pb-3 text-slate-400 text-xs font-semibold uppercase tracking-wider">Owner</th>
                        <th className="pb-3 text-slate-400 text-xs font-semibold uppercase tracking-wider text-right">Leads</th>
                        <th className="pb-3 text-slate-400 text-xs font-semibold uppercase tracking-wider text-right">Pipeline</th>
                        <th className="pb-3 text-slate-400 text-xs font-semibold uppercase tracking-wider text-right">Won</th>
                        <th className="pb-3 text-slate-400 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell" style={{minWidth: 120}}>Distribution</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...owners].sort((a, b) => b[1].total - a[1].total).map(([name, d]) => (
                        <tr key={name} className="border-b border-slate-800/50">
                          <td className="py-3 text-white text-sm font-medium">{name}</td>
                          <td className="py-3 text-slate-300 text-sm text-right">{d.count}</td>
                          <td className="py-3 text-emerald-400 text-sm font-medium text-right">${d.total.toLocaleString()}</td>
                          <td className="py-3 text-cyan-400 text-sm font-medium text-right">${d.won.toLocaleString()}{d.won_count > 0 && <span className="text-slate-500 text-xs ml-1">({d.won_count})</span>}</td>
                          <td className="py-3 hidden sm:table-cell">
                            <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                              <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-500 transition-all duration-500" style={{ width: `${(d.total / maxTotal) * 100}%` }} />
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </div>
        </>
      )}
    </div>
  );
}
